import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function LeavesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "", leave_type: "casual", reason: "" });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/leaves"); setRows(data); }
    catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/leaves", form); toast.success("Leave requested"); setOpen(false); setForm({ start_date: "", end_date: "", leave_type: "casual", reason: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Failed to submit leave")); }
  };

  const decide = async (id, status) => {
    try { await api.patch(`/leaves/${id}`, { status }); toast.success(`Leave ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Failed to update leave")); }
  };

  return (
    <div>
      <PageHeader
        testid="page-leaves"
        eyebrow="Time off"
        title="Leaves."
        description={user.role === "employee" ? "Request time off. HR reviews here." : "Review pending leave requests from your workforce."}
        actions={
          user.role === "employee" && (
            <button onClick={() => setOpen(true)} data-testid="btn-request-leave" className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2.5 font-mono text-sm hover:bg-neutral-950 transition-colors">
              <Plus className="w-4 h-4" /> Request leave
            </button>
          )
        }
      />

      <div className="p-8">
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="leaves-table">
            <thead className="bg-[#FAFAFA] border-b border-neutral-200">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                {user.role !== "employee" && <th className="px-4 py-3">Employee</th>}
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">To</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Status</th>
                {user.role !== "employee" && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-6 text-neutral-500 font-mono">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-neutral-500 font-mono">No leave records.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-[#FAFAFA] transition-colors">
                  {user.role !== "employee" && (
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.employee_name}</div>
                      <div className="text-xs text-neutral-500 font-mono">{r.employee_code}</div>
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono capitalize">{r.leave_type}</td>
                  <td className="px-4 py-3 font-mono">{r.start_date}</td>
                  <td className="px-4 py-3 font-mono">{r.end_date}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{r.reason || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${
                      r.status === "approved" ? "border-[#002FA7] text-[#002FA7]" :
                      r.status === "rejected" ? "border-[#FF3B30] text-[#FF3B30]" :
                      "border-neutral-300 text-neutral-600"
                    }`}>{r.status}</span>
                  </td>
                  {user.role !== "employee" && (
                    <td className="px-4 py-3 text-right">
                      {r.status === "pending" ? (
                        <>
                          <button onClick={() => decide(r.id, "approved")} data-testid={`approve-${r.id}`} className="inline-flex items-center gap-1 text-[#002FA7] hover:text-neutral-950 font-mono text-xs mr-3 transition-colors">
                            <Check className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button onClick={() => decide(r.id, "rejected")} data-testid={`reject-${r.id}`} className="inline-flex items-center gap-1 text-[#FF3B30] hover:text-neutral-950 font-mono text-xs transition-colors">
                            <X className="w-3.5 h-3.5" /> Reject
                          </button>
                        </>
                      ) : <span className="text-xs text-neutral-400 font-mono">—</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-neutral-950/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-neutral-950 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="dialog-new-leave">
            <div className="border-b border-neutral-200 px-6 py-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#002FA7]">Request</div>
              <div className="font-display font-bold text-2xl tracking-tighter">Time off</div>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">From *</label>
                <input required type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} data-testid="leave-start"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">To *</label>
                <input required type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} data-testid="leave-end"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Type</label>
                <select value={form.leave_type} onChange={(e) => setForm({...form, leave_type: e.target.value})} data-testid="leave-type"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm bg-white">
                  <option value="casual">Casual</option>
                  <option value="sick">Sick</option>
                  <option value="earned">Earned</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Reason</label>
                <textarea rows={3} value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} data-testid="leave-reason"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2.5 font-mono text-sm hover:bg-neutral-100 transition-colors">Cancel</button>
                <button type="submit" data-testid="btn-submit-leave" className="px-5 py-2.5 bg-[#002FA7] text-white font-mono text-sm hover:bg-neutral-950 transition-colors">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
